/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import VideoHero from './components/VideoHero';
import VideoFilters from './components/VideoFilters';
import VideoGrid from './components/VideoGrid';
import VideoCta from './components/VideoCta';
import WhyWeFilmEverything from './components/WhyWeFilmEverything';
import { videosMock } from '../../mocks/videos';
import { useSEOHead } from '../../hooks/useSEOHead';
import { buildVideoObjectSchema, injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema, injectVideoListSchema, removeVideoListSchema } from '../../lib/safeSchema';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { pageFaqs } from '../../mocks/pageFaqs';

export default function VideosPage() {
  useSEOHead({
    title: 'Used Car Videos & Walkthroughs | Warrenton OR | Rockhound',
    description: 'Watch detailed video tours of our used cars, trucks, and SUVs.',
    keywords: 'car videos, vehicle tours, virtual car tour, used car videos',
    canonicalUrl: 'https://rockhoundauto.com/videos',
  });

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const categories = ['all', 'walkaround', 'test-drive', 'features', 'customer-story'];

  const filteredVideos = videosMock.filter((video) => {
    const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
    const matchesSearch = !searchQuery || video.title.toLowerCase().includes(searchQuery.toLowerCase()) || video.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  /** Parse "MM:SS" or "H:MM:SS" duration strings into total seconds */
  const parseDurationToSeconds = (d: string): number => {
    const parts = d.split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return 0;
  };

  const totalSeconds = filteredVideos.reduce(
    (acc, v) => acc + parseDurationToSeconds(v.duration),
    0
  );
  const totalMinutes = Math.round(totalSeconds / 60);
  const watchLabel =
    totalMinutes >= 60
      ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m total`
      : `${totalMinutes} min total`;

  // Check if the entire mock array is empty (no videos uploaded yet)
  const noVideosUploaded = videosMock.length === 0;

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Videos',
          item: 'https://rockhoundauto.com/videos',
        },
      ],
    };

    injectJsonLd('schema-videos-breadcrumb', breadcrumbSchema);

    // Inject ItemList wrapper schema using safe wrapper
    injectVideoListSchema(
      'schema-videos-list',
      filteredVideos,
      'https://rockhoundauto.com/videos#videolist'
    );

    // Inject individual VideoObject schemas for each video
    filteredVideos.forEach((video) => {
      const videoSchema = buildVideoObjectSchema({
        id: video.id,
        title: video.title,
        description: video.description,
        platform: video.platform,
        videoId: video.videoId,
        thumbnail: video.thumbnail,
        publishedDate: video.publishedDate,
        duration: video.duration,
        views: video.views,
        year: video.year,
        make: video.make,
        model: video.model,
      });
      injectJsonLd(`schema-video-${video.id}`, videoSchema);
    });

    return () => {
      removeJsonLd('schema-videos-breadcrumb');
      removeVideoListSchema('schema-videos-list');
      // Clean up all individual video schemas
      filteredVideos.forEach((video) => {
        removeJsonLd(`schema-video-${video.id}`);
      });
    };
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    injectFAQSchema(
      'schema-videos-faq',
      pageFaqs.videos,
      'https://rockhoundauto.com/videos#faqpage'
    );

    return () => {
      removeFAQSchema('schema-videos-faq');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Videos' }]} />
      <VideoHero />
      <WhyWeFilmEverything />
      <div className="bg-[#F7F5F0] min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!noVideosUploaded && (
            <>
              <VideoFilters categories={categories} selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} searchQuery={searchQuery} onSearchChange={setSearchQuery} />
              <div className="mb-6 flex items-baseline gap-3">
                <h2 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E]">
                  {filteredVideos.length} {filteredVideos.length === 1 ? 'Video' : 'Videos'}
                </h2>
                {filteredVideos.length > 0 && (
                  <span className="text-sm text-gray-500 font-medium">· {watchLabel}</span>
                )}
              </div>
            </>
          )}
          
          {noVideosUploaded ? (
            <div className="bg-white rounded-2xl border border-[#E8E2D6] p-16 text-center max-w-2xl mx-auto">
              <div className="w-20 h-20 flex items-center justify-center bg-[#E8B84B]/10 rounded-full mx-auto mb-6">
                <i className="ri-video-add-line text-5xl text-[#E8B84B]"></i>
              </div>
              <h3 className="font-['Oswald'] text-3xl font-bold text-[#1C2B1E] mb-3">No Videos Uploaded Yet</h3>
              <p className="text-lg text-gray-600 mb-2 leading-relaxed">
                We're working on creating detailed video walkthroughs of our inventory.
              </p>
              <p className="text-base text-gray-500 mb-8">
                Check back soon to see full tours, test drives, and feature highlights!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/inventory"
                  className="inline-flex items-center justify-center gap-2 bg-[#1C2B1E] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2d4430] transition-colors whitespace-nowrap"
                >
                  <i className="ri-car-line text-xl"></i>
                  Browse Available Inventory
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#1C2B1E] border-2 border-[#1C2B1E] px-8 py-4 rounded-lg font-semibold hover:bg-[#F7F5F0] transition-colors whitespace-nowrap"
                >
                  <i className="ri-question-line text-xl"></i>
                  Ask About a Vehicle
                </Link>
              </div>
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E8E2D6] p-12 text-center">
              <i className="ri-video-line text-6xl text-gray-300 mb-4 block"></i>
              <h3 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-2">No videos found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filter</p>
              <button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }} className="px-6 py-3 bg-[#1C2B1E] text-white rounded-full font-medium hover:bg-[#2d4430] transition-colors whitespace-nowrap cursor-pointer">View All Videos</button>
            </div>
          ) : (
            <>
              <VideoGrid videos={filteredVideos} />
              <div className="mt-12"><VideoCta /></div>
            </>
          )}
        </div>
      </div>
    </>
  );
}